import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, MapPin } from 'lucide-react';
import { flowOverlay, flowPanel } from '../ui/modalSpacing';
import Tooltip from '../ui/Tooltip';
import { TYPE } from '../../constants/typeScale';
import ReactFlow, {
  Background, BackgroundVariant, Controls, MiniMap, Panel, ReactFlowProvider, applyNodeChanges,
  useReactFlow, useViewport,
  type Node, type NodeProps, type NodeChange,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { resolveDownloadUrl } from '../../services/storageService';
import ShotNode, { type ShotNodeData } from './ShotNode';
import { sceneBands, shotX, dropIndex, NODE_H, ROW_Y, SCENE_PAD, SHOT_GAP } from './boardLayout';
import MusicLane from './MusicLane';
import ShotInspector from './ShotInspector';
import BoardContextMenu, { type MenuItem } from './BoardContextMenu';
import TransitionEdge, { transitionEdges } from './TransitionEdge';
import { useStorageUrl } from '../chatHome/MemberAvatar';
import type { StoryboardShot, MusicSpan } from '../../services/studioService';

/**
 * STUDIO BOARD — the episode as a canvas of connected shot cards.
 *
 * Built on React Flow, but constrained to what a STORY board means rather than what a graph editor allows:
 *
 *  - **No arbitrary edges.** `nodesConnectable` is off and no handles are rendered. A creator cannot wire shot
 *    1 into shot 5; the only relationship is "then", and it is implied by order.
 *  - **Position is derived, never stored.** Every node's x/y comes from `boardLayout` off the shots array, so
 *    the board can never disagree with the order the episode plays. Dragging REORDERS the array.
 *  - **Scenes are a background layer, not parent nodes.** React Flow's own guidance is against parent/child
 *    nesting for this, and a change of parent is not reported on drop — so scenes are drawn behind the cards.
 *
 * On SIZE: the shot count is NOT fixed — a creator adds and removes shots as the episode takes shape, and the
 * current cap (MAX_SHOTS) is a product decision that can move. So the architecture is chosen to tolerate growth
 * rather than to exploit a small number: DOM nodes + SVG stay comfortable into the hundreds, and the reported
 * degradation threshold is in the thousands. If a series-wide view ever approaches that, the fix is
 * level-of-detail (simplified cards at low zoom), not a rewrite onto canvas.
 *
 * Why React Flow at all? Not node count — the tedious correctness: trackpad pinch-zoom, viewport maths under
 * resize, selection, the minimap, and keyboard panning.
 */
export interface StudioBoardProps {
  shots: StoryboardShot[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  /** Commit a reorder — the parent owns the shots array. */
  onReorder: (from: number, to: number) => void;
  /** Which shot is mid-render, if any. */
  renderingIndex?: number | null;
  /** LEGACY override — approved-for-spend now lives on the shot itself (`shot.locked`). Retained so a caller
   *  that still passes indices keeps working; the shot's own flag wins. */
  lockedIndices?: number[];
  /** Per-shot seconds, when the parent has worked them out. */
  secondsFor?: (index: number) => number | null;
  /** Add a shot to the end of the episode. Omitted when the board is at its cap. */
  onAddShot?: () => void;
  /** Patch one shot — used by the seam to set the transition INTO the following shot. */
  onShotChange?: (index: number, patch: Partial<StoryboardShot>) => void;
  /** Re-make a single shot's clip (charges again, so it is single-flight). */
  onRemakeShot?: (index: number) => void;
  /** Which shot is currently being re-made. */
  remakingIndex?: number | null;
  /** Ask a specialist to set this shot — the AGENT ENGINE path (studioAssistShot). */
  onAssistShot?: (index: number) => void;
  /**
   * WHERE EACH SCENE HAPPENS (RF3-WORKFLOW §4). The board already draws scene bands — runs of shots sharing a
   * place — but a band was only ever labelled "Scene 1". Naming the PROJECT location a band belongs to is what
   * lets every shot in it reuse that place's wording, and its anchor object, byte-identically at render time.
   *
   * The band's location is read from, and written to, the shot that OPENS it: bands are derived from
   * `continuesScene`, so there is no band entity to hang it on, and the opening shot is the one that survives
   * a reorder of the shots after it.
   */
  locations?: Array<{ id: string; name: string; image_path?: string | null }>;
  onSetSceneLocation?: (openingShotIndex: number, locationId: string) => void;
  /**
   * THE PROJECT'S OBJECTS, and which are in a given shot (RF3-SCHEMA-REVIEW seam 1). A hero object is stated
   * once at project level with locked wording; a shot naming it gets that wording pasted in byte-identically
   * at render time. Before this the project's objects reached nothing — the board read a separate, unreachable
   * episode-level list.
   */
  objects?: Array<{ id: string; name: string; image_path?: string | null }>;
  onSetShotObjects?: (shotIndex: number, propIds: string[]) => void;
  /** The project's PEOPLE, and who is in a given shot (RF3-SCHEMA-REVIEW seam 2). */
  people?: Array<{ id: string; name: string; image_path?: string | null }>;
  /** The episode these shots belong to, so the inspector can ask what each shot's references will be (F4). */
  storyboardId?: string | null;
  /** Bumped whenever the episode is written; the inspector re-asks the server after a save lands. */
  savedTick?: number;
  /** How many shots the server holds; a shot at or past this index is not saved yet, so nobody asks about it. */
  savedShotCount?: number;
  onSetShotPeople?: (shotIndex: number, characterIds: string[]) => void;
  /** Promote an archived take back to live (studioRestoreTake). Free — the clip already exists. */
  onRestoreTake?: (index: number, clipPath: string) => void;
  /** Which shot has a specialist call in flight. */
  assistingIndex?: number | null;
  /** Structural verbs every canvas has. Omit one and its control disables itself rather than vanishing. */
  onDuplicateShot?: (index: number) => void;
  onDeleteShot?: (index: number) => void;
  /** Render the episode — ComfyUI's Queue Prompt, in our words. Omit to hide the panel entirely. */
  /**
   * Queue a render at one of the two SPEEDS (owner 2026-08-31). A draft is for finding out whether the shots
   * work; a final render is the one that can go on a store. Both are offered, because guessing which the
   * creator means would either overcharge them or hand them a cut they cannot publish.
   */
  onQueueRender?: (quality: 'draft' | 'final') => void;
  /** True while a render is in flight, so the button reports rather than inviting a second one. */
  queueing?: boolean;
  /** The music lane under the board. Omit to hide it. */
  musicSpans?: MusicSpan[];
  onMusicSpansChange?: (spans: MusicSpan[]) => void;
  /** True when no more shots may be added — the affordance explains itself rather than vanishing. */
  atShotCap?: boolean;
  className?: string;
}

/** The trailing "add a shot" card. A node, not floating chrome, so it sits where the next shot will appear. */
const AddShotNode: React.FC<NodeProps<{ onAdd?: () => void; atCap?: boolean }>> = ({ data }) => (
  <button
    type="button"
    data-testid="add-shot"
    onClick={data.onAdd}
    disabled={data.atCap || !data.onAdd}
    title={data.atCap ? 'This episode is at its shot limit' : 'Add a shot to the end'}
    className="flex h-[300px] w-[172px] flex-col items-center justify-center gap-2 rounded-[10px] border border-dashed
               border-secondary-300 text-secondary-500 transition-colors hover:border-secondary-500 hover:text-secondary-700 dark:border-secondary-700 dark:hover:text-secondary-300
               disabled:opacity-40 disabled:hover:border-secondary-300 dark:disabled:hover:border-secondary-700"
  >
    <Plus className="h-5 w-5" />
    <span className="text-[11px]">{data.atCap ? 'Shot limit reached' : 'Add a shot'}</span>
  </button>
);

/** A scene frame. A node — NOT an overlay — because only nodes live inside React Flow's viewport transform;
 *  an absolutely-positioned SVG drifts away from the cards the moment the board is panned or zoomed. */
const SceneNode: React.FC<NodeProps<{ label: string; w: number; h: number; place?: string; onPickPlace?: () => void }>> = ({ data }) => (
  <div
    className="pointer-events-none rounded-2xl border border-dashed border-secondary-300 bg-black/[.015] dark:border-secondary-700/70 dark:bg-white/[.012]"
    style={{ width: data.w, height: data.h }}
  >
    <span className="absolute left-4 top-2 flex items-center gap-2 text-[10.5px] uppercase tracking-[.14em] text-secondary-500 dark:text-secondary-600">
      {data.label}
      {/* WHERE this run of shots happens. The frame itself is pointer-events-none so a drag over it reaches
          the canvas; this one control opts back in, because it is the only thing on the frame you can press. */}
      {data.onPickPlace && (
        <button
          type="button"
          onClick={data.onPickPlace}
          className="pointer-events-auto inline-flex items-center gap-1 rounded px-1 py-0.5 normal-case tracking-normal
                     text-[10.5px] text-secondary-500 underline decoration-dotted transition-colors
                     hover:text-secondary-800 dark:hover:text-secondary-200"
        >
          <MapPin className="h-3 w-3" aria-hidden />
          {data.place || 'set the place'}
        </button>
      )}
    </span>
  </div>
);

const nodeTypes = {
  scene: SceneNode as React.ComponentType<NodeProps<any>>,
  shot: ShotNode as React.ComponentType<NodeProps<ShotNodeData>>,
  addShot: AddShotNode as React.ComponentType<NodeProps<any>>,
};

/** One edge type: the link between consecutive shots, labelled with the transition. */
const edgeTypes = { transition: TransitionEdge };

const Board: React.FC<StudioBoardProps> = ({
  shots, selectedIndex, onSelect, onReorder, renderingIndex, lockedIndices, secondsFor,
  onAddShot, atShotCap, onShotChange, onRemakeShot, remakingIndex, musicSpans, onMusicSpansChange,
  onAssistShot, assistingIndex, onDuplicateShot, onDeleteShot, onQueueRender, queueing, onRestoreTake,
  locations, onSetSceneLocation, objects, onSetShotObjects, people, onSetShotPeople, storyboardId, savedTick, savedShotCount,
  className = '',
}) => {
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  // Collapsed nodes are a VIEW concern (ComfyUI's node collapse) — nothing about the episode changes, so this
  // is deliberately board-local and never persisted onto the shots.
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  /**
   * Which take each shot is currently SHOWING. 0 = the live clip, 1..n = archived takes (newest first).
   * View state only: browsing an older take changes nothing about the episode until the creator promotes it.
   */
  const [takeView, setTakeView] = useState<Record<number, number>>({});
  const stepTake = useCallback((i: number, delta: number, count: number) => setTakeView((prev) => ({
    ...prev, [i]: Math.max(0, Math.min(count - 1, (prev[i] || 0) + delta)),
  })), []);
  const toggleCollapse = useCallback((i: number) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (!next.delete(i)) next.add(i);
    return next;
  }), []);
  // What we just did, announced to screen readers. A drag is invisible to assistive tech, so the keyboard
  // path needs to SAY what happened — otherwise reordering is a silent, unverifiable action.
  const [announce, setAnnounce] = useState('');

  // Resolve thumbnails by PATH, so a regenerated frame (new path) re-resolves instead of showing a stale image.
  useEffect(() => {
    let alive = true;
    shots.forEach((s) => {
      // Archived takes are resolved alongside the live clip: flipping to take 2 must show take 2, not a blank.
      const takePaths = (s.takes || []).map((t) => t.clipPath);
      [s.previewFramePath || s.startFramePath, s.clipPath, ...takePaths].forEach((p) => {
      if (p && !thumbs[p]) {
        resolveDownloadUrl(p)
          .then((u) => { if (alive) setThumbs((prev) => ({ ...prev, [p]: u })); })
          .catch(() => { /* a missing frame is a blank card, never a broken board */ });
      }
      });
    });
    return () => { alive = false; };
  }, [shots, thumbs]);

  const bands = useMemo(() => sceneBands(shots), [shots]);
  /** The opening shot of the scene a given shot belongs to — where that scene's location is recorded. */
  const bandStartFor = useCallback(
    (i: number) => bands.find((b) => i >= b.start && i < b.start + b.count)?.start ?? i,
    [bands],
  );
  /** Which band is choosing its place, by the index of the shot that opens it. Null = the picker is closed. */
  const [placingBand, setPlacingBand] = useState<number | null>(null);
  /** Which shot is choosing the objects in it. Null = closed. */
  const [propsForShot, setPropsForShot] = useState<number | null>(null);
  /** Which shot is choosing who is in it. Null = closed. */
  const [peopleForShot, setPeopleForShot] = useState<number | null>(null);

  /**
   * ESCAPE CLOSES WHICHEVER PICKER IS OPEN.
   *
   * Each picker used to carry `onKeyDown` on its own overlay div. A div is not focusable, so unless the
   * creator happened to have tabbed into something inside it the key event never arrived and Escape did
   * nothing — found by the creation-flow review walk 2026-09-12. A modal that ignores Escape is a modal a
   * keyboard user has to hunt for the way out of, and the Done button is not a substitute: dismissal should
   * cost one key, the same key, everywhere.
   *
   * Listening on the document is what makes that true regardless of where focus actually sits.
   */
  const anyPickerOpen = placingBand !== null || propsForShot !== null || peopleForShot !== null;
  useEffect(() => {
    if (!anyPickerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Only ever closes a picker — the pickers write on click, so there is nothing to discard here.
      setPlacingBand(null);
      setPropsForShot(null);
      setPeopleForShot(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [anyPickerOpen]);
  const locked = useMemo(() => new Set(lockedIndices || []), [lockedIndices]);

  const nodes = useMemo<Node<ShotNodeData>[]>(() => shots.map((shot, i) => {
    const p = shot.previewFramePath || shot.startFramePath;
    const takeCount = 1 + (shot.takes?.length || 0);
    const view = Math.min(takeView[i] || 0, takeCount - 1);
    const viewedTake = view > 0 ? shot.takes?.[view - 1] : undefined;
    // The frame shown follows the take being VIEWED, so the stepper actually changes what you are judging.
    const shownClip = viewedTake ? thumbs[viewedTake.clipPath] : (shot.clipPath ? thumbs[shot.clipPath] : null);
    return {
      id: `shot-${i}`,
      type: 'shot',
      position: { x: shotX(shots, i, bands), y: ROW_Y },
      // Dragging is horizontal only: the board is a line, so vertical freedom would just let a creator
      // build a crooked row that means nothing.
      dragHandle: undefined,
      data: {
        shot,
        number: i + 1,
        thumb: p ? thumbs[p] : null,
        selected: selectedIndex === i,
        rendering: renderingIndex === i,
        locked: shot.locked === true || locked.has(i),
        seconds: secondsFor?.(i) ?? null,
        clipUrl: shownClip,
        onRemake: onRemakeShot ? () => onRemakeShot(i) : undefined,
        remaking: remakingIndex === i,
        remakeBusy: remakingIndex !== null && remakingIndex !== undefined,
        onLineChange: onShotChange ? (line: string) => onShotChange(i, { line }) : undefined,
        takeIndex: view,
        takeCount,
        onStepTake: takeCount > 1 ? (d: number) => stepTake(i, d, takeCount) : undefined,
        onUseTake: view > 0 && onRestoreTake && viewedTake
          ? () => { onRestoreTake(i, viewedTake.clipPath); setTakeView((prev) => ({ ...prev, [i]: 0 })); }
          : undefined,
        collapsed: collapsed.has(i),
        onToggleCollapse: () => toggleCollapse(i),
      },
    };
  }), [shots, bands, thumbs, selectedIndex, renderingIndex, locked, secondsFor, onRemakeShot, remakingIndex,
       onShotChange, collapsed, toggleCollapse, takeView, stepTake, onRestoreTake]);

  /** Seams + the trailing add-card. Both are furniture: never draggable, never selectable, never a "shot". */
  const allNodes = useMemo<Node<any>[]>(() => {
    // Scene frames FIRST and behind: zIndex keeps them under the cards they enclose.
    const out: Node<any>[] = bands.map((b) => ({
      id: `scene-${b.start}`, type: 'scene',
      position: { x: b.x, y: ROW_Y - 34 },
      draggable: false, selectable: false, focusable: false, zIndex: -1,
      data: {
        label: b.label,
        w: b.width,
        h: NODE_H + 52,
        // The band's place lives on the shot that OPENS it — bands are derived, so there is no band entity to
        // hold it, and the opening shot survives a reorder of the shots after it.
        place: locations?.find((l) => l.id === shots[b.start]?.location_id)?.name,
        onPickPlace: onSetSceneLocation && locations?.length ? () => setPlacingBand(b.start) : undefined,
      },
    }));
    out.push(...nodes);

    if (onAddShot || atShotCap) {
      const last = bands[bands.length - 1];
      const x = last ? last.x + last.width - SCENE_PAD + SHOT_GAP : SCENE_PAD;
      out.push({
        id: 'add-shot', type: 'addShot', position: { x, y: ROW_Y },
        draggable: false, selectable: false,
        data: { onAdd: onAddShot, atCap: atShotCap },
      });
    }
    return out;
  }, [nodes, bands, onAddShot, atShotCap, shots, locations, onSetSceneLocation]);

  /**
   * WHAT IS IN A SHOT — one multi-select, used for both objects and people, so the two can never drift into
   * different interactions for the same question. A modal, like every other picker in the product.
   */
  /**
   * THE PICTURE BESIDE THE NAME (F4). Choosing "Mara" from a list of names is choosing a string; choosing her
   * FACE is choosing the thing the shot will actually be built from — which is what "visually lock down the
   * reference" means. A row with no plate yet simply shows no thumbnail rather than a broken frame, because
   * the absence is already reported, with its fix, in the inspector strip.
   */
  const PickerThumb: React.FC<{ path?: string | null; name: string }> = ({ path, name }) => {
    const url = useStorageUrl(path || null);
    if (!path || !url) return null;
    return (
      <img
        src={url}
        alt={name}
        className="h-7 w-7 shrink-0 rounded border border-secondary-200 object-cover dark:border-secondary-700"
      />
    );
  };

  const shotPicker = (
    shotIndex: number,
    title: string,
    items: Array<{ id: string; name: string; image_path?: string | null }>,
    selected: string[],
    onToggle: (next: string[]) => void,
    onClose: () => void,
    testid: string,
  ) => (
    <div
      className={`${flowOverlay} z-50`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={testid}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      <div className={`${flowPanel} sm:max-w-sm sm:h-auto`}>
        <div className="flex items-center gap-2 border-b border-secondary-200 px-4 py-3 dark:border-secondary-600">
          <h2 className="min-w-0 flex-1 text-sm font-semibold text-secondary-900 dark:text-white">{title}</h2>
        </div>
        <ul className="p-2">
          {items.map((it) => {
            const on = selected.includes(it.id);
            return (
              <li key={it.id}>
                <button
                  type="button"
                  data-testid={`pick-${testid}-${it.id}`}
                  onClick={() => onToggle(on ? selected.filter((x) => x !== it.id) : [...selected, it.id])}
                  className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[13px]
                             transition-colors hover:bg-secondary-100 dark:hover:bg-secondary-800 ${
                               on ? 'font-medium text-secondary-900 dark:text-white' : 'text-secondary-700 dark:text-secondary-300'}`}
                >
                  <span className={`inline-block h-3 w-3 shrink-0 rounded-sm border ${
                    on ? 'border-secondary-900 bg-secondary-900 dark:border-white dark:bg-white' : 'border-secondary-300 dark:border-secondary-600'}`} />
                  <PickerThumb path={it.image_path} name={it.name} />
                  {it.name}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="flex justify-end border-t border-secondary-200 px-3 py-2 dark:border-secondary-600">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-xs text-secondary-500 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );

  /**
   * WHICH PLACE THIS RUN OF SHOTS HAPPENS IN. Kept SEPARATE from the multi-select above rather than folded
   * into it: a scene has exactly one place, and it needs an explicit "not one of these" — a creator who picked
   * the wrong location needs a way out that is not picking a different wrong one.
   */
  const placePicker = placingBand !== null && locations?.length && onSetSceneLocation ? (
    <div
      className={`${flowOverlay} z-50`}
      role="dialog"
      aria-modal="true"
      aria-label="Where does this scene happen?"
      data-testid="scene-place-picker"
      onKeyDown={(e) => { if (e.key === 'Escape') setPlacingBand(null); }}
    >
      <div className={`${flowPanel} sm:max-w-sm sm:h-auto`}>
        <div className="flex items-center gap-2 border-b border-secondary-200 px-4 py-3 dark:border-secondary-600">
          <MapPin className="h-4 w-4 shrink-0 text-secondary-400" aria-hidden />
          <h2 className="min-w-0 flex-1 text-sm font-semibold text-secondary-900 dark:text-white">
            Where does this scene happen?
          </h2>
        </div>
        <ul className="p-2">
          {locations.map((l) => (
            <li key={l.id}>
              <button
                type="button"
                data-testid={`pick-place-${l.id}`}
                onClick={() => { onSetSceneLocation(placingBand, l.id); setPlacingBand(null); }}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[13px]
                           transition-colors hover:bg-secondary-100 dark:hover:bg-secondary-800 ${
                             shots[placingBand]?.location_id === l.id
                               ? 'font-medium text-secondary-900 dark:text-white'
                               : 'text-secondary-700 dark:text-secondary-300'}`}
              >
                <PickerThumb path={l.image_path} name={l.name} />
                {l.name}
              </button>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between gap-2 border-t border-secondary-200 px-3 py-2 dark:border-secondary-600">
          <button
            type="button"
            onClick={() => { onSetSceneLocation(placingBand, ''); setPlacingBand(null); }}
            className="rounded-lg px-2 py-1 text-xs text-secondary-500 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800"
          >
            Not one of these
          </button>
          <button
            type="button"
            onClick={() => setPlacingBand(null)}
            className="rounded-lg px-2 py-1 text-xs text-secondary-500 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const objectPicker = propsForShot !== null && objects?.length && onSetShotObjects
    ? shotPicker(
      propsForShot,
      `What is in shot ${propsForShot + 1}?`,
      objects,
      shots[propsForShot]?.prop_ids || [],
      (next) => onSetShotObjects(propsForShot, next),
      () => setPropsForShot(null),
      'shot-objects-picker',
    )
    : null;

  const peoplePicker = peopleForShot !== null && people?.length && onSetShotPeople
    ? shotPicker(
      peopleForShot,
      `Who is in shot ${peopleForShot + 1}?`,
      people,
      shots[peopleForShot]?.character_ids || [],
      (next) => onSetShotPeople(peopleForShot, next),
      () => setPeopleForShot(null),
      'shot-people-picker',
    )
    : null;

  /** The links. Derived from order, so a reorder re-routes them without any bookkeeping. */
  const edges = useMemo(() => transitionEdges(
    shots,
    (i) => bands.some((b) => b.start === i),        // this shot opens a scene, so the link into it is a cut
    onShotChange,
  ), [shots, bands, onShotChange]);

  /**
   * Live positions. Node positions are DERIVED from the shots array — but React Flow has to be allowed to move
   * a card while it is being dragged, or the card never leaves its slot and the drop lands exactly where it
   * started (which read as "drag does nothing"). So: hold the derived layout in state, let React Flow apply
   * position changes during a drag, then on drop convert the position to an index and re-derive from props.
   */
  const [live, setLive] = useState<Node<any>[]>(allNodes);
  const dragging = useRef(false);
  useEffect(() => { if (!dragging.current) setLive(allNodes); }, [allNodes]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setLive((prev) => {
      const next = applyNodeChanges(changes, prev);       // let the card follow the cursor

      for (const c of changes) {
        if (c.type === 'position' && c.id.startsWith('shot-')) {
          if (c.dragging) { dragging.current = true; continue; }
          if (c.dragging === false) {
            dragging.current = false;
            const from = Number(c.id.replace('shot-', ''));
            // React Flow's FINAL position change carries `dragging:false` but often no `position` — so read
            // where the card actually ended up from the applied state rather than from the event. Reading the
            // event was why a drag appeared to do nothing: the drop resolved to the slot it started in.
            const dropped = next.find((n) => n.id === c.id)?.position;
            if (dropped) {
              const to = dropIndex(shots, from, dropped.x);
              if (to !== from) { onReorder(from, to); return next; }
            }
            return allNodes;                              // unmoved: snap back to the derived layout
          }
        }
        if (c.type === 'select' && c.selected && c.id.startsWith('shot-')) {
          onSelect(Number(c.id.replace('shot-', '')));
        }
      }
      return next;
    });
  }, [shots, allNodes, onReorder, onSelect]);

  /* ── The conventional canvas chrome: a selection toolbar, a right-click menu, and the standard keymap. ──
     None of this is invented. Figma/Miro float the actions above the selection; ComfyUI/Miro/Figma all put
     the same verbs behind a right-click; and the shortcuts below are the ones those tools already trained
     creators to expect. The board's job is to recognise them, not to have opinions about them. */
  const { fitView, zoomTo, zoomIn, zoomOut } = useReactFlow();
  // Only the zoom is still read: the x/y offsets existed to place a floating toolbar in screen space.
  const { zoom } = useViewport();
  const [menu, setMenu] = useState<{ x: number; y: number; items: MenuItem[] } | null>(null);

  /**
   * The board's own box, used by the double-click guard and by layout.
   *
   * A `toolbarAt` memo lived here, converting canvas coordinates to screen coordinates every render so a
   * floating bar could track the selected card through pan and zoom — and clamping it so a card near an edge
   * did not push its own actions off-canvas. All of that machinery existed to make a floating panel behave,
   * and none of it is needed by a docked one, which is a large part of the argument for docking.
   */
  const wrapRef = useRef<HTMLDivElement>(null);

  /** The verbs for one shot — shared by the inspector and the right-click menu so they can never disagree. */
  const shotItems = useCallback((i: number): MenuItem[] => [
    { label: 'Ask a specialist', hint: 'A', onSelect: () => onAssistShot?.(i), disabled: !onAssistShot },
    { label: 'Re-make this shot', hint: 'R', onSelect: () => onRemakeShot?.(i), disabled: !onRemakeShot || remakingIndex != null },
    { label: shots[i]?.locked ? 'Remove approval' : 'Approve for spend', hint: 'L',
      onSelect: () => onShotChange?.(i, { locked: !shots[i]?.locked }), disabled: !onShotChange, divider: true },
    { label: 'Duplicate', hint: '\u2318D', onSelect: () => onDuplicateShot?.(i), disabled: !onDuplicateShot },
    { label: 'Delete', hint: '\u232B', onSelect: () => onDeleteShot?.(i), disabled: !onDeleteShot },
  ], [onAssistShot, onRemakeShot, onDuplicateShot, onDeleteShot, remakingIndex, onShotChange, shots]);

  /** The verbs for the canvas itself. */
  const canvasItems = useCallback((): MenuItem[] => [
    { label: 'Add a shot', onSelect: () => onAddShot?.(), disabled: !onAddShot || !!atShotCap },
    { label: 'Zoom to fit', hint: '\u21e71', onSelect: () => fitView({ padding: 0.25, duration: 200 }), divider: true },
    { label: 'Zoom to 100%', hint: '\u21e70', onSelect: () => zoomTo(1, { duration: 200 }) },
  ], [onAddShot, atShotCap, fitView, zoomTo]);

  /**
   * Position a menu relative to the BOARD, not the page — the board is not always full-bleed.
   * Measured off the container ref rather than `e.currentTarget`: for a node right-click the current target
   * is the CARD, so measuring it put the menu wherever the card happened to be instead of under the cursor.
   */
  const openMenu = useCallback((e: React.MouseEvent, items: MenuItem[]) => {
    e.preventDefault();
    const r = wrapRef.current?.getBoundingClientRect();
    setMenu({ x: e.clientX - (r?.left || 0), y: e.clientY - (r?.top || 0), items });
  }, []);

  /**
   * Keyboard reordering. Dragging is a pointer gesture, so without this the board is unusable by keyboard —
   * and reorder is the board's primary verb. Alt+Arrow moves the SELECTED shot one place, matching the
   * convention used for reorderable lists (Alt so plain arrows stay free for panning the canvas).
   */
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Never steal a key from a field — a creator editing a shot line types D and R constantly.
    const t = e.target as HTMLElement;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;

    // ── Canvas-level shortcuts, exactly as Figma defines them ──
    if (e.shiftKey && e.key === '1') { e.preventDefault(); fitView({ padding: 0.25, duration: 200 }); return; }
    if (e.shiftKey && e.key === '0') { e.preventDefault(); zoomTo(1, { duration: 200 }); return; }
    if ((e.metaKey || e.ctrlKey) && (e.key === '=' || e.key === '+')) { e.preventDefault(); zoomIn({ duration: 150 }); return; }
    if ((e.metaKey || e.ctrlKey) && e.key === '-') { e.preventDefault(); zoomOut({ duration: 150 }); return; }
    if (e.key === 'Escape') { setMenu(null); return; }

    if (selectedIndex == null) return;

    // ── Selection-level shortcuts ──
    if ((e.metaKey || e.ctrlKey) && (e.key === 'd' || e.key === 'D')) {
      e.preventDefault(); onDuplicateShot?.(selectedIndex); return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault(); onDeleteShot?.(selectedIndex); return;
    }
    if ((e.key === 'l' || e.key === 'L') && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      onShotChange?.(selectedIndex, { locked: !shots[selectedIndex]?.locked });
      setAnnounce(shots[selectedIndex]?.locked ? 'Approval removed.' : 'Shot approved for spend.');
      return;
    }

    const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!dir) return;
    const to = selectedIndex + dir;
    if (to < 0 || to >= shots.length) return;
    e.preventDefault();

    // Alt+Arrow REORDERS (the list convention); a bare arrow just walks the selection, which is what every
    // canvas does and what makes the board reviewable without touching the mouse.
    if (e.altKey) {
      onReorder(selectedIndex, to);
      onSelect(to);                    // keep the moved shot selected, so it can be moved again
      setAnnounce(`Shot moved to position ${to + 1} of ${shots.length}.`);
    } else {
      onSelect(to);
      setAnnounce(`Shot ${to + 1} of ${shots.length} selected.`);
    }
  }, [selectedIndex, shots, onReorder, onSelect, onDuplicateShot, onDeleteShot, onShotChange, fitView, zoomTo, zoomIn, zoomOut]);

  return (
    <div
      className={`flex h-full w-full flex-col outline-none ${className}`}
      onKeyDown={onKeyDown}
      // Double-click empty canvas to add — ComfyUI and Miro both do this, and it is the fastest way to grow a
      // board. React Flow v11 exposes no `onPaneDoubleClick`, so it is handled here.
      //
      // The guard is GEOMETRIC, and deliberately so. React Flow's pane sits above the viewport for pan and
      // selection hit-testing, so it reports itself both as `e.target` AND via `elementFromPoint` even when the
      // pointer is squarely on a node — both of those checks were tried and both let a double-click on a card
      // add a shot. Asking whether the point falls inside a node's rect cannot be fooled by stacking order.
      onDoubleClick={(e) => {
        const onNode = Array.from(wrapRef.current?.querySelectorAll('.react-flow__node') || [])
          .some((n) => {
            const r = n.getBoundingClientRect();
            return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
          });
        if (onNode) return;
        if (onAddShot && !atShotCap) onAddShot();
      }}
      tabIndex={0}
      role="application"
      aria-label="Shot board — arrow keys move between shots, Alt and arrow reorders, right-click for more"
    >
      <p className="sr-only" role="status" aria-live="polite">{announce}</p>

      {/* The canvas gets its own box, and the music lane sits BELOW it rather than floating over it. As an
          overlay the lane covered the bottom of the viewport that `fitView` had just fitted into, so the last
          row of a board was always clipped behind it. */}
      <div className="flex min-h-0 flex-1">
      <div ref={wrapRef} className="relative min-h-0 flex-1">
      <ReactFlow
        nodes={live}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onNodeClick={(_, n) => { if (n.id.startsWith('shot-')) onSelect(Number(n.id.replace('shot-', ''))); }}
        onPaneClick={() => setMenu(null)}
        onPaneContextMenu={(e) => openMenu(e as unknown as React.MouseEvent, canvasItems())}
        onNodeContextMenu={(e, n) => {
          if (!n.id.startsWith('shot-')) return;
          const i = Number(n.id.replace('shot-', ''));
          onSelect(i);
          openMenu(e as unknown as React.MouseEvent, shotItems(i));
        }}
        nodesConnectable={false}         /* a story is a sequence — never a wiring diagram */
        nodesFocusable
        elementsSelectable
        panOnScroll
        selectionOnDrag={false}
        zoomOnDoubleClick={false}
        minZoom={0.3}
        maxZoom={1.6}
        fitView
        fitViewOptions={{ padding: 0.25, maxZoom: 1 }}
        /* The canvas is OUR product surface — a third-party watermark on it reads as unfinished, and the
           creator has no idea what React Flow is. (Their licence permits hiding it on a paid plan; revisit
           if that ever changes.) */
        proOptions={{ hideAttribution: true }}
        className="bg-secondary-50 dark:bg-secondary-950"
      >
        <Background variant={BackgroundVariant.Dots} gap={26} size={1} className="!text-secondary-300 dark:!text-secondary-700" color="currentColor" />
        <Controls showInteractive={false} className="!border-secondary-200 !bg-white dark:!border-secondary-700 dark:!bg-secondary-900" />
        {onQueueRender && (
          <Panel position="top-right" className="!m-2">
            <div className="flex items-center gap-2 rounded-lg border border-secondary-200 bg-white/95 p-1.5 shadow-xl backdrop-blur
                            dark:border-secondary-700 dark:bg-secondary-900/95">
              {/* ComfyUI names the node it is executing rather than showing a bare percentage, because "which
                  one is this?" is the question a creator actually has while waiting. Same here: the shot
                  number while rendering, the completed count otherwise. */}
              <span className="px-1 font-mono text-[11px] text-secondary-500" data-testid="queue-count">
                {renderingIndex != null
                  ? `shot ${renderingIndex + 1} of ${shots.length}`
                  : `${shots.filter((x) => x.clipPath).length}/${shots.length}`}
              </span>
              {/* Draft sits FIRST because it is the one a creator reaches for most: the board is somewhere you
                  try things, and trying things has to be the cheap gesture or nobody tries anything. It stays
                  quiet (a bordered button) so the finishing act keeps the emphasis. */}
              {/* The native `title=` is banned here (house rule): ~1.5s to appear, invisible on touch, and
                  un-styleable — on the two controls that decide how much a creator spends, an explanation that
                  arrives a second and a half late is an explanation they never read. */}
              <Tooltip content="A quick, low-cost pass so you can see whether these shots work. Not for publishing.">
                <button
                  type="button"
                  data-testid="queue-draft"
                  onClick={() => onQueueRender('draft')}
                  disabled={queueing || !shots.length}
                  className={`rounded-md border border-secondary-300 px-2.5 py-1.5 ${TYPE.meta} font-semibold text-secondary-700
                             transition-colors hover:text-secondary-900 disabled:cursor-not-allowed disabled:opacity-40
                             dark:border-secondary-600 dark:text-secondary-200 dark:hover:text-white`}
                >
                  Quick rough cut
                </button>
              </Tooltip>
              <Tooltip content="The finished cut at full quality — the only version you can put on your store.">
                <button
                  type="button"
                  data-testid="queue-render"
                  onClick={() => onQueueRender('final')}
                  disabled={queueing || !shots.length}
                  className={`rounded-md bg-secondary-900 px-3 py-1.5 ${TYPE.meta} font-semibold text-white dark:bg-white dark:text-secondary-900
                             transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  {queueing ? 'Making the film…' : 'Make the film'}
                </button>
              </Tooltip>
            </div>
          </Panel>
        )}
        <Panel position="bottom-left" className="!mb-1 !ml-11">
          <button
            type="button"
            data-testid="zoom-readout"
            onClick={() => fitView({ padding: 0.25, duration: 200 })}
            title="Zoom to fit (Shift+1)"
            className="rounded border border-secondary-200 bg-white px-2 py-1 font-mono text-[11px]
                       text-secondary-500 transition-colors hover:text-secondary-900 dark:border-secondary-700
                       dark:bg-secondary-900 dark:text-secondary-400 dark:hover:text-white"
          >
            {Math.round(zoom * 100)}%
          </button>
        </Panel>
        {/* A minimap earns its space only when there is more board than fits. On a short episode it was pure
            clutter sitting over the canvas — Figma and Miro both keep navigation aids out of the way until
            the document is big enough to need them. */}
        {shots.length > 5 && (
          <MiniMap
            pannable zoomable
            nodeColor="#5E5E68"
            maskColor="rgba(10,10,12,.72)"
            className="!border !border-secondary-200 !bg-white dark:!border-secondary-700 dark:!bg-secondary-900"
          />
        )}
      </ReactFlow>

      {/* Rendered outside ReactFlow so the viewport transform never scales or clips it. */}
      {placePicker}
      {objectPicker}
      {peoplePicker}

      {menu && <BoardContextMenu x={menu.x} y={menu.y} items={menu.items} onClose={() => setMenu(null)} />}
      </div>

      {/* THE INSPECTOR, docked (owner 2026-08-30). It used to be a bar floating over the card — see
          ShotInspector's header for why that pattern was abandoned here and at Figma. It sits INSIDE the
          centre pane, so the workstation is still three panes. */}
      <ShotInspector
        shot={selectedIndex != null ? shots[selectedIndex] || null : null}
        index={selectedIndex}
        onChange={onShotChange}
        onAssist={onAssistShot}
        onRemake={onRemakeShot}
        onDuplicate={onDuplicateShot}
        onDelete={onDeleteShot}
        onPickPeople={people?.length && onSetShotPeople ? (i) => setPeopleForShot(i) : undefined}
        onPickObjects={objects?.length && onSetShotObjects ? (i) => setPropsForShot(i) : undefined}
        /* A place belongs to the SCENE, not to one shot, so both of these resolve the selected shot to the band
           it sits in and act on that band's opening shot — which is where `location_id` lives. */
        onPickPlace={locations?.length && onSetSceneLocation
          ? (i) => setPlacingBand(bandStartFor(i))
          : undefined}
        placeName={selectedIndex != null
          ? locations?.find((l) => l.id === shots[bandStartFor(selectedIndex)]?.location_id)?.name || null
          : null}
        storyboardId={storyboardId}
        savedTick={savedTick}
        savedShotCount={savedShotCount}
        assisting={assistingIndex === selectedIndex}
        remaking={remakingIndex === selectedIndex}
        busy={remakingIndex != null}
      />
      </div>

      {/* The music lane is a TIME ruler under the board, not a node on it: a bed spans shots, so it belongs
          alongside the sequence rather than inside it. */}
      {musicSpans && onMusicSpansChange && shots.length > 0 && (
        <div className="shrink-0 border-t border-secondary-200 bg-white px-3 py-2 dark:border-secondary-800 dark:bg-secondary-950">
          <MusicLane spans={musicSpans} shotCount={shots.length} onChange={onMusicSpansChange} />
        </div>
      )}
    </div>
  );
};

/** React Flow needs its provider in scope for the viewport hooks the minimap and controls use. */
const StudioBoard: React.FC<StudioBoardProps> = (props) => (
  <ReactFlowProvider><Board {...props} /></ReactFlowProvider>
);
export default StudioBoard;
